import React from "react";
import dayjs from "dayjs";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    padding: 32,
    lineHeight: 1.6,
  },
  image: {
    height: 24,
  },
  heading: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: "bold",
    color: "#333",
  },
  subheading: {
    fontSize: 16,
    marginTop: 10,
    marginBottom: 10,
    fontWeight: "bold",
    color: "#333",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  column: {
    width: "48%",
  },
  field: {
    fontWeight: "bold",
  },
  multilineField: {
    marginTop: 5,
  },
  line: {
    height: 1,
    backgroundColor: "grey",
    width: "100%",
    marginVertical: 8,
  },
});

const formatDate = (value) => {
  if (!value) return "";
  const date = dayjs(value);
  return date.isValid() ? date.format("MM-DD-YYYY") : "";
};

const joinAddress = (line1, line2, city, state, zip) => {
  const lineParts = [line1, line2].filter((part) => part && String(part).trim());
  const cityStateZip = [city, state, zip]
    .filter((part) => part && String(part).trim())
    .join(" ");
  return [lineParts.join(", "), cityStateZip]
    .filter((part) => part && String(part).trim())
    .join(", ");
};

const extractWoundValues = (details, key) => {
  if (!Array.isArray(details) || details.length === 0) return "";
  const values = details
    .map((detail) => detail?.[key])
    .filter((val) => val !== null && val !== undefined && String(val).trim() !== "");
  return values.length ? values.join(", ") : "";
};

const formatInsuranceStatus = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const compact = raw.toLowerCase().replace(/[^a-z]/g, "");
  switch (compact) {
    case "pending":
      return "Pending";
    case "covered":
      return "Covered";
    case "submitted":
      return "Submitted";
    case "inforequired":
      return "Info Required";
    case "notcovered":
      return "Not Covered";
    default:
      return raw;
  }
};

const IVRSubmissionPdf = ({
  ivrData,
  woundDetails,
  ivrProductName,
  ivrProductStatus,
}) => {
  const submissionDate = formatDate(ivrData?.SubmissionDate);
  const patientDob = formatDate(ivrData?.PatientDOB);
  const dateOfProcedure = formatDate(ivrData?.DateOfProcedure);
  const productName =
    typeof ivrProductName === "string" && ivrProductName.trim()
      ? ivrProductName.trim()
      : "N/A";
  const productStatusDisplay = formatInsuranceStatus(ivrProductStatus) || "N/A";
  const address = joinAddress(
    ivrData?.AccountAddressLine1,
    ivrData?.AccountAddressLine2,
    ivrData?.AccountCity,
    ivrData?.AccountState,
    ivrData?.AccountZip
  );
  const woundType = extractWoundValues(woundDetails, "WoundType");
  const woundSize = extractWoundValues(woundDetails, "WoundSize");
  const applicationCpt = extractWoundValues(woundDetails, "ApplicationCPT");
  const icd10Codes = extractWoundValues(woundDetails, "ICDD10DiagnosisCode");
  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.row}>
          <Text style={styles.heading}>IVR Submission</Text>
          <Image
            style={styles.image}
            src={`${process.env.PUBLIC_URL}/companyLogo.png`}
          />
        </View>
        <View style={styles.line} />
        <View style={styles.section}>
          <Text>
            <Text style={styles.field}>IVRAS ID:</Text> {ivrData?.IVRASID}
          </Text>
          <Text>
            <Text style={styles.field}>Account Name:</Text> {ivrData?.AccountName}
          </Text>
          <Text>
            <Text style={styles.field}>Submitted By:</Text> {ivrData?.SubmittedBy}
          </Text>
          <Text>
            <Text style={styles.field}>Submitted Date:</Text> {submissionDate}
          </Text>
          <Text>
            <Text style={styles.field}>IVR Product:</Text> {productName}
          </Text>
          <Text>
            <Text style={styles.field}>Insurance Status:</Text> {productStatusDisplay}
          </Text>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Account Details</Text>
        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Place of Service:</Text> {ivrData?.PlaceOfService}
              </Text>
              <Text>
                <Text style={styles.field}>Contact Name:</Text> {ivrData?.AccountContactName}
              </Text>
              <Text>
                <Text style={styles.field}>Physician Name:</Text> {ivrData?.PhysicianName}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Account Phone:</Text> {ivrData?.AccountPhone}
              </Text>
              <Text>
                <Text style={styles.field}>Account Fax:</Text> {ivrData?.AccountFax}
              </Text>
              <Text>
                <Text style={styles.field}>Medicare Admin Contractor:</Text> {ivrData?.MedicareAdminContractor}
              </Text>
            </View>
          </View>

          <Text>
            <Text style={styles.field}>Account Address:</Text> {address}
          </Text>
          <Text>
            <Text style={styles.field}>NPI:</Text> {ivrData?.NPI}
          </Text>
          <Text>
            <Text style={styles.field}>TIN:</Text> {ivrData?.TIN}
          </Text>
          <Text>
            <Text style={styles.field}>PTAN:</Text> {ivrData?.PTAN}
          </Text>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Patient Details</Text>
        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Patient First Name:</Text> {ivrData?.PatientFirstName}
              </Text>
              <Text>
                <Text style={styles.field}>Patient Last Name:</Text> {ivrData?.PatientLastName}
              </Text>
              <Text>
                <Text style={styles.field}>Patient DOB:</Text> {patientDob}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Patient Phone:</Text> {ivrData?.PatientPhone}
              </Text>
              <Text>
                <Text style={styles.field}>Patient Email:</Text> {ivrData?.PatientEmail}
              </Text>
              <Text>
                <Text style={styles.field}>Okay to Contact Patient:</Text> {ivrData?.OkayToContactPatient}
              </Text>
            </View>
          </View>

          <Text>
            <Text style={styles.field}>Patient Address:</Text>
          </Text>
          <Text style={styles.multilineField}>
            {ivrData?.PatientAddressLine1}, {ivrData?.PatientAddressLine2}
          </Text>
          <Text style={styles.multilineField}>
            {ivrData?.PatientCity}, {ivrData?.PatientState} {ivrData?.PatientZip}
          </Text>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Primary Insurance</Text>
        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Insurance Name:</Text> {ivrData?.PrimaryInsuranceName}
              </Text>
              <Text>
                <Text style={styles.field}>Policy Number:</Text> {ivrData?.PolicyNumber}
              </Text>
              <Text>
                <Text style={styles.field}>Subscriber Name:</Text> {ivrData?.SubscriberName}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Type Of Plan:</Text> {ivrData?.TypeOfPlan}
              </Text>
              <Text>
                <Text style={styles.field}>Insurance Phone Number:</Text> {ivrData?.InsurancePhoneNumber}
              </Text>
              <Text>
                <Text style={styles.field}>Provider In Network:</Text> {ivrData?.ProviderInNetwork}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Secondary Insurance</Text>
        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Insurance Name:</Text> {ivrData?.SecondaryInsuranceName}
              </Text>
              <Text>
                <Text style={styles.field}>Policy Number:</Text> {ivrData?.SecondaryPolicyNumber}
              </Text>
              <Text>
                <Text style={styles.field}>Subscriber Name:</Text> {ivrData?.SecondarySubscriberName}
              </Text>
            </View>
            <View style={styles.column}>
              <Text>
                <Text style={styles.field}>Type Of Plan:</Text> {ivrData?.SecondaryTypeOfPlan}
              </Text>
              <Text>
                <Text style={styles.field}>Insurance Phone Number:</Text> {ivrData?.SecondaryInsurancePhoneNumber}
              </Text>
              <Text>
                <Text style={styles.field}>Provider In Network:</Text> {ivrData?.SecondaryProviderInNetwork}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Other Details</Text>
        <View style={styles.section}>
          <Text>
            <Text style={styles.field}>Wound Type:</Text> {woundType || ivrData?.WoundType}
          </Text>
          <Text>
            <Text style={styles.field}>Wound Size:</Text> {woundSize || ivrData?.WoundSize}
          </Text>
          <Text>
            <Text style={styles.field}>Application CPT:</Text> {applicationCpt || ivrData?.ApplicationCPT}
          </Text>
          <Text>
            <Text style={styles.field}>ICD-10 Diagnosis Code:</Text> {icd10Codes || ivrData?.ICD10}
          </Text>
          <Text>
            <Text style={styles.field}>Is patient currently residing in SNF?:</Text> {ivrData?.InSNF}
          </Text>
          <Text>
            <Text style={styles.field}>Is the patient under a surgical global period?:</Text> {ivrData?.SurgicalGlobalPeriod}
          </Text>
          <Text>
            <Text style={styles.field}>Is Patient currently in Hospice?:</Text> {ivrData?.PatientInHospice}
          </Text>
          <Text>
            <Text style={styles.field}>CPT Code:</Text> {ivrData?.CPTCode}
          </Text>
          <Text>
            <Text style={styles.field}>Date of Procedure:</Text> {dateOfProcedure}
          </Text>
        </View>
        <View style={styles.line} />
        <Text style={styles.subheading}>Signature</Text>
        <View style={styles.section}>
          <Text>
            By signing below, I certify that I have received the necessary
            patient authorization to release the medical and/or other patient
            information referenced on the form relating to the above-referenced
            patient. This information is for verifying insurance coverage,
            seeking reimbursement, and the sole purpose of claim support.
          </Text>
          <View style={styles.line} />
          <Text>
            <Text style={styles.field}>Digitally Signed By</Text> {ivrData?.Signature} on {submissionDate}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const normalizePdfOptions = (options) => {
  if (!options) return {};
  if (typeof options === "string") return { ivrProductName: options };
  if (Array.isArray(options)) return { ivrProductGroups: options };
  return options;
};

const createIVRSubmissionPdfBlob = async (ivrDataOrPayload, options) => {
  if (!ivrDataOrPayload) return null;
  const ivrData =
    ivrDataOrPayload?.ivrData ?? ivrDataOrPayload;
  const woundDetails =
    ivrDataOrPayload?.woundDetails ??
    ivrDataOrPayload?.WoundDetails ??
    ivrData?.WoundDetails ??
    [];
  const { ivrProductName, ivrProductStatus } = normalizePdfOptions(options);
  return pdf(
    <IVRSubmissionPdf
      ivrData={ivrData}
      woundDetails={woundDetails}
      ivrProductName={ivrProductName}
      ivrProductStatus={ivrProductStatus}
    />
  ).toBlob();
};

export { IVRSubmissionPdf, createIVRSubmissionPdfBlob };
